import React, { useState, useEffect } from 'react';
import { ref, get, set, remove, push } from 'firebase/database';
import { database } from '../../firebase';

interface Announcement {
  id: string;
  title: string;
  content: string;
  type: 'info' | 'warning' | 'success' | 'error';
  targetAudience: 'all' | 'students' | 'premium';
  createdAt: number;
  expiresAt?: number;
  active: boolean;
}

export const Announcements: React.FC = () => {
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingAnnouncement, setEditingAnnouncement] = useState<Announcement | null>(null);

  const [newAnnouncement, setNewAnnouncement] = useState<Partial<Announcement>>({
    title: '',
    content: '',
    type: 'info',
    targetAudience: 'all',
    active: true
  });

  useEffect(() => {
    fetchAnnouncements();
  }, []);

  const fetchAnnouncements = async () => {
    try {
      const announcementsRef = ref(database, 'announcements');
      const snapshot = await get(announcementsRef);
      
      if (snapshot.exists()) {
        const announcementsData = snapshot.val();
        const announcementsList = Object.entries(announcementsData).map(([id, data]: [string, any]) => ({
          id,
          ...data
        }));
        setAnnouncements(announcementsList.sort((a, b) => b.createdAt - a.createdAt));
      }
    } catch (error) {
      console.error('Error fetching announcements:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveAnnouncement = async () => {
    try {
      const announcementData = {
        ...newAnnouncement,
        createdAt: editingAnnouncement ? editingAnnouncement.createdAt : Date.now()
      };

      if (editingAnnouncement) {
        const announcementRef = ref(database, `announcements/${editingAnnouncement.id}`);
        await set(announcementRef, announcementData);
        setAnnouncements(announcements.map(a => 
          a.id === editingAnnouncement.id ? { ...announcementData, id: editingAnnouncement.id } as Announcement : a
        ));
      } else {
        const announcementsRef = ref(database, 'announcements');
        const newAnnouncementRef = push(announcementsRef);
        await set(newAnnouncementRef, announcementData);
        setAnnouncements([{ ...announcementData, id: newAnnouncementRef.key } as Announcement, ...announcements]);
      }
      
      resetForm();
    } catch (error) {
      console.error('Error saving announcement:', error);
    }
  };

  const deleteAnnouncement = async (id: string) => {
    if (!confirm('Are you sure you want to delete this announcement?')) return;
    
    try {
      const announcementRef = ref(database, `announcements/${id}`);
      await remove(announcementRef);
      setAnnouncements(announcements.filter(a => a.id !== id));
    } catch (error) {
      console.error('Error deleting announcement:', error);
    }
  };

  const toggleActive = async (id: string, active: boolean) => {
    try {
      const announcementRef = ref(database, `announcements/${id}/active`);
      await set(announcementRef, active);
      setAnnouncements(announcements.map(a => 
        a.id === id ? { ...a, active } : a
      ));
    } catch (error) {
      console.error('Error updating announcement status:', error);
    }
  };

  const resetForm = () => {
    setNewAnnouncement({
      title: '',
      content: '',
      type: 'info',
      targetAudience: 'all',
      active: true
    });
    setShowAddForm(false);
    setEditingAnnouncement(null);
  };

  const startEdit = (announcement: Announcement) => {
    setNewAnnouncement(announcement);
    setEditingAnnouncement(announcement);
    setShowAddForm(true);
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'info': return 'bg-blue-100 text-blue-800';
      case 'warning': return 'bg-yellow-100 text-yellow-800';
      case 'success': return 'bg-green-100 text-green-800';
      case 'error': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'info': return 'ℹ️';
      case 'warning': return '⚠️';
      case 'success': return '✅';
      case 'error': return '❌';
      default: return 'ℹ️';
    }
  };

  const AnnouncementForm = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-lg font-semibold">
              {editingAnnouncement ? 'Edit Announcement' : 'Create New Announcement'}
            </h3>
            <button onClick={resetForm} className="text-gray-400 hover:text-gray-600">✕</button>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
              <input
                type="text"
                value={newAnnouncement.title}
                onChange={(e) => setNewAnnouncement({...newAnnouncement, title: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                placeholder="Enter announcement title"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Content</label>
              <textarea
                value={newAnnouncement.content}
                onChange={(e) => setNewAnnouncement({...newAnnouncement, content: e.target.value})}
                rows={5}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                placeholder="Enter announcement content"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                <select
                  value={newAnnouncement.type}
                  onChange={(e) => setNewAnnouncement({...newAnnouncement, type: e.target.value as any})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                >
                  <option value="info">Info</option>
                  <option value="warning">Warning</option>
                  <option value="success">Success</option>
                  <option value="error">Error</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Target Audience</label>
                <select
                  value={newAnnouncement.targetAudience}
                  onChange={(e) => setNewAnnouncement({...newAnnouncement, targetAudience: e.target.value as any})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                >
                  <option value="all">All Users</option>
                  <option value="students">Students Only</option>
                  <option value="premium">Premium Users</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Expires At (Optional)</label>
              <input
                type="datetime-local"
                value={newAnnouncement.expiresAt ? new Date(newAnnouncement.expiresAt).toISOString().slice(0, 16) : ''}
                onChange={(e) => setNewAnnouncement({
                  ...newAnnouncement, 
                  expiresAt: e.target.value ? new Date(e.target.value).getTime() : undefined
                })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="active"
                checked={newAnnouncement.active}
                onChange={(e) => setNewAnnouncement({...newAnnouncement, active: e.target.checked})}
                className="mr-2"
              />
              <label htmlFor="active" className="text-sm font-medium text-gray-700">Active</label>
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <button
                onClick={resetForm}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300"
              >
                Cancel
              </button>
              <button
                onClick={saveAnnouncement}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
              >
                {editingAnnouncement ? 'Update' : 'Create'} Announcement
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          {[1, 2, 3].map(i => (
            <div key={i} className="h-20 bg-gray-200 rounded"></div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-lg font-semibold">Announcements ({announcements.length})</h3>
        <button
          onClick={() => setShowAddForm(true)}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
        >
          Create Announcement
        </button>
      </div>

      <div className="space-y-4">
        {announcements.map((announcement) => (
          <div key={announcement.id} className="border border-gray-200 rounded-lg p-4">
            <div className="flex justify-between items-start mb-3">
              <div className="flex-1">
                <div className="flex items-center space-x-2 mb-2">
                  <span className="text-lg">{getTypeIcon(announcement.type)}</span>
                  <h4 className="text-lg font-medium text-gray-900">{announcement.title}</h4>
                  <span className={`px-2 py-1 text-xs rounded ${getTypeColor(announcement.type)}`}>
                    {announcement.type}
                  </span>
                  <span className={`px-2 py-1 text-xs rounded ${
                    announcement.active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                  }`}>
                    {announcement.active ? 'Active' : 'Inactive'}
                  </span>
                </div>
                <p className="text-sm text-gray-600 mb-2">{announcement.content}</p>
                <div className="flex items-center space-x-4 text-xs text-gray-500">
                  <span>👥 {announcement.targetAudience}</span>
                  <span>📅 {new Date(announcement.createdAt).toLocaleDateString()}</span>
                  {announcement.expiresAt && (
                    <span>⏰ Expires: {new Date(announcement.expiresAt).toLocaleDateString()}</span>
                  )}
                </div>
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => toggleActive(announcement.id, !announcement.active)}
                  className={`px-3 py-1 text-xs rounded ${
                    announcement.active 
                      ? 'bg-red-100 text-red-700 hover:bg-red-200' 
                      : 'bg-green-100 text-green-700 hover:bg-green-200'
                  }`}
                >
                  {announcement.active ? 'Deactivate' : 'Activate'}
                </button>
                <button
                  onClick={() => startEdit(announcement)}
                  className="px-3 py-1 text-xs bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
                >
                  Edit
                </button>
                <button
                  onClick={() => deleteAnnouncement(announcement.id)}
                  className="px-3 py-1 text-xs bg-red-100 text-red-700 rounded hover:bg-red-200"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {showAddForm && <AnnouncementForm />}
    </div>
  );
};